import React, { createContext, useContext, useEffect, ReactNode, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18n from '../i18n'; // Importer l'instance configurée
// Retirer l'import direct des traductions et types spécifiques
// import translations from '@/locales'; 
// import type { TranslationKeys } from '@/locales/types';

// Simplifier le type du contexte, car `t` viendra de `useTranslation`
interface LanguageContextType {
  currentLanguage: string;
  defaultLanguage: string;
  supportedLanguages: string[];
  changeLanguage: (language: string) => void;
  getUrlWithLanguage: (path: string, lang?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const DEFAULT_LANGUAGE = 'fr';
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'es']; // Garder pour la validation et getUrl

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ lang?: string }>();
  
  // Déterminer la langue initiale à partir de l'URL ou utiliser la langue par défaut
  const initialLanguage = params.lang && SUPPORTED_LANGUAGES.includes(params.lang) 
    ? params.lang 
    : DEFAULT_LANGUAGE;

  // Changer la langue dans i18next lorsque l'URL change ou au montage initial
  useEffect(() => {
    const langToSet = params.lang && SUPPORTED_LANGUAGES.includes(params.lang) ? params.lang : DEFAULT_LANGUAGE;
    if (i18n.language !== langToSet) {
      i18n.changeLanguage(langToSet);
    }
  }, [params.lang]);

  // Fonction pour changer la langue et naviguer
  const changeLanguage = useCallback((language: string) => {
    if (SUPPORTED_LANGUAGES.includes(language) && language !== i18n.language) {
      const currentPathWithoutLang = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '$1'); // Extraire le chemin sans le préfixe
      const newPath = `/${language}${currentPathWithoutLang || ''}`;
      
      // Changer la langue dans i18next AVANT de naviguer peut être plus fluide
      i18n.changeLanguage(language).then(() => {
          navigate(newPath + location.search); // Naviguer vers la nouvelle URL
      });
    }
  }, [location.pathname, location.search, navigate]);

  // Fonction utilitaire pour construire les URLs préfixées
  const getUrlWithLanguage = useCallback((path: string, lang?: string) => {
    const targetLanguage = lang || i18n.language || DEFAULT_LANGUAGE;
    const langPrefix = `/${targetLanguage}`;

    // Si le chemin commence déjà par un préfixe de langue supporté, le remplacer
    const langRegex = new RegExp(`^\/(${SUPPORTED_LANGUAGES.join('|')})(\/|$)`);
    if (langRegex.test(path)) {
      return path.replace(langRegex, `${langPrefix}$2`);
    }

    // Sinon, ajouter le préfixe
    return path.startsWith('/') 
      ? `${langPrefix}${path}` 
      : `${langPrefix}/${path}`;
  }, []); // i18n.language est réactif via useTranslation, mais on le lit directement ici pour la synchronicité de l'URL

  // Le contexte expose maintenant la langue actuelle via i18n.language,
  // les fonctions de changement et de construction d'URL.
  // La fonction `t` sera obtenue via le hook `useTranslation` dans les composants.
  const contextValue: LanguageContextType = {
    currentLanguage: i18n.language || initialLanguage, // Fournir une valeur synchrone initiale
    defaultLanguage: DEFAULT_LANGUAGE,
    supportedLanguages: SUPPORTED_LANGUAGES,
    changeLanguage,
    getUrlWithLanguage,
  };
  
  // Mettre à jour la valeur du contexte si la langue i18next change
  // (Bien que les composants utilisant useTranslation se mettront à jour d'eux-mêmes)
   useEffect(() => {
     const handleLanguageChanged = (lng: string) => {
       // Forcer une mise à jour potentielle du contexte si nécessaire, bien que peu probable
       // car les composants utiliseront useTranslation qui est déjà réactif.
       // Ce contexte devient plus un fournisseur d'utilitaires (changeLang, getUrl).
       console.log('i18next language changed to:', lng); 
     };
     i18n.on('languageChanged', handleLanguageChanged);
     return () => {
       i18n.off('languageChanged', handleLanguageChanged);
     };
   }, []);


  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={contextValue}>
        {children}
      </LanguageContext.Provider>
    </I18nextProvider>
  );
};

// Hook pour accéder aux fonctions utilitaires (changeLanguage, getUrlWithLanguage)
// Pour la traduction, utiliser `useTranslation()` de `react-i18next` directement.
export const useLanguageUtils = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageUtils must be used within a LanguageProvider');
  }
  return context;
};

// Hook combiné pour la compatibilité avec le code existant
export const useLanguage = () => {
  const utils = useLanguageUtils();
  const { t, i18n } = useTranslation();
  return { 
    ...utils, 
    t,
    i18n
  };
};
